<script setup>
// §8.6 / Addendum §3 DataTable — sortable, selectable, tabular-nums.
// Addendum upgrades:
//   • Sticky header (§3.1). Focusables carry scroll-margin (base.css) so the
//     sticky header never obscures a tab-focused control (WCAG 2.4.11).
//   • Frozen columns (§3.2): sticky left with cumulative offsets, an opaque
//     --surface background, inherited row-hover, and a right-edge shadow shown
//     ONLY while scrolled — the shadow is the affordance that off-screen columns
//     exist, not decoration.
//   • Row actions (§3.3): always in the DOM AND visible (rendered at --text-3,
//     rising to --text on hover/focus-within) — never opacity:0-until-hover,
//     which is invisible to keyboard and touch. ≤2 inline; ≥3 → kebab menu, via
//     the #row-actions slot.
//   • No virtualisation (§3.4) — it breaks find-in-page and printing.
// Columns: [{ key, label, align?, numeric?, sortable?, sortBy?, frozen? }].
//   sortBy: another row field to sort this column by, when its shown value doesn't sort the way it should be read
//   (a stage name sorts by the stage's position in the pipeline, not alphabetically).
import { ref, computed, onMounted, onBeforeUnmount, nextTick } from 'vue'
import LCheckbox from '../inputs/Checkbox.vue'
import LIcon from '../base/Icon.vue'

const props = defineProps({
	columns: { type: Array, required: true },
	rows: { type: Array, required: true },
	rowKey: { type: String, default: 'id' },
	selectable: { type: Boolean, default: false },
	stickyHeader: { type: Boolean, default: true },
	rowLabelKey: { type: String, default: null }, // for accessible action labels
})
const selected = defineModel('selected', { default: () => [] })

const sortKey = ref(null)
const sortDir = ref('asc')
function toggleSort(col)
{
	if (!col.sortable) return
	if (sortKey.value === col.key) sortDir.value = sortDir.value === 'asc' ? 'desc' : 'asc'
	else { sortKey.value = col.key; sortDir.value = 'asc' }
}
const sortedRows = computed(() =>
{
	if (!sortKey.value) return props.rows
	const dir = sortDir.value === 'asc' ? 1 : -1
	const col = props.columns.find((c) => c.key === sortKey.value)
	const field = col?.sortBy || sortKey.value
	return [...props.rows].sort((a, b) =>
	{
		const av = a[field], bv = b[field]
		if (typeof av === 'number' && typeof bv === 'number') return (av - bv) * dir
		return String(av).localeCompare(String(bv)) * dir
	})
})

const allSelected = computed(() =>
{
	if (!props.rows.length) return false
	if (selected.value.length === props.rows.length) return true
	return selected.value.length ? 'indeterminate' : false
})
function toggleAll(v) { selected.value = v ? props.rows.map((r) => r[props.rowKey]) : [] }
function isSelected(r) { return selected.value.includes(r[props.rowKey]) }
function toggleRow(r, v)
{
	const k = r[props.rowKey]
	selected.value = v ? [...selected.value, k] : selected.value.filter((x) => x !== k)
}

// --- frozen columns -------------------------------------------------------
const hasFrozen = computed(() => props.columns.some((c) => c.frozen))
const wrap = ref(null)
const headRow = ref(null)
const scrolledX = ref(false)
const offsets = ref({}) // colKey -> left px ; '__sel' for the select column
const lastFrozenKey = ref(null)

function measure()
{
	if (!headRow.value || !hasFrozen.value) return
	const cells = [...headRow.value.children]
	let left = 0
	const next = {}
	let idxOffset = 0
	if (props.selectable)
	{
		next['__sel'] = 0
		left += cells[0].getBoundingClientRect().width
		idxOffset = 1
	}
	props.columns.forEach((col, i) =>
	{
		if (col.frozen)
		{
			next[col.key] = left
			left += cells[i + idxOffset].getBoundingClientRect().width
			lastFrozenKey.value = col.key
		}
	})
	offsets.value = next
}
function onScroll() { if (wrap.value) scrolledX.value = wrap.value.scrollLeft > 0 }

let ro
onMounted(async () =>
{
	await nextTick()
	measure()
	wrap.value?.addEventListener('scroll', onScroll, { passive: true })
	if (window.ResizeObserver && wrap.value)
	{
		ro = new ResizeObserver(() => measure())
		ro.observe(wrap.value)
	} else
	{
		window.addEventListener('resize', measure)
	}
})
onBeforeUnmount(() =>
{
	wrap.value?.removeEventListener('scroll', onScroll)
	ro?.disconnect()
	window.removeEventListener('resize', measure)
})

function frozenStyle(key)
{
	if (!hasFrozen.value || offsets.value[key] == null) return null
	return { position: 'sticky', left: offsets.value[key] + 'px', zIndex: 1 }
}
function frozenClass(key)
{
	return {
		frozen: hasFrozen.value && offsets.value[key] != null,
		'frozen-edge': key === lastFrozenKey.value && scrolledX.value,
	}
}
const selFrozen = computed(() => hasFrozen.value && props.selectable)
function rowLabel(r)
{
	return props.rowLabelKey ? r[props.rowLabelKey] : ''
}
</script>

<template>
	<div ref="wrap" class="l-table-wrap" :class="{ 'sticky-head': stickyHeader }">
		<table class="l-table">
			<thead>
				<tr ref="headRow">
					<th v-if="selectable" class="sel"
						:class="{ frozen: selFrozen, 'frozen-edge': selFrozen && !columns.some(c => c.frozen) && scrolledX }"
						:style="selFrozen ? { position: 'sticky', left: '0px', zIndex: 3 } : null">
						<LCheckbox :model-value="allSelected" @update:model-value="toggleAll" />
					</th>
					<th v-for="col in columns" :key="col.key"
						:class="[{ right: col.align === 'right' || col.numeric, sortable: col.sortable }, frozenClass(col.key)]"
						:style="frozenStyle(col.key) ? { ...frozenStyle(col.key), zIndex: 3 } : null"
						:aria-sort="sortKey === col.key ? (sortDir === 'asc' ? 'ascending' : 'descending') : undefined">
						<button v-if="col.sortable" class="th-btn" type="button" @click="toggleSort(col)">
							{{ col.label }}
							<LIcon name="chevron-up" v-if="sortKey === col.key && sortDir === 'asc'" :size="12" />
							<LIcon name="chevron-down" v-else-if="sortKey === col.key" :size="12" />
							<LIcon name="sort" v-else :size="12" class="idle" />
						</button>
						<span v-else>{{ col.label }}</span>
					</th>
					<th v-if="$slots['row-actions']" class="actions-head"><span class="sr-only">Actions</span></th>
				</tr>
			</thead>
			<tbody>
				<tr v-for="r in sortedRows" :key="r[rowKey]" :class="{ on: isSelected(r) }">
					<td v-if="selectable" class="sel"
						:class="{ frozen: selFrozen, 'frozen-edge': selFrozen && !columns.some(c => c.frozen) && scrolledX }"
						:style="selFrozen ? { position: 'sticky', left: '0px' } : null">
						<LCheckbox :model-value="isSelected(r)" @update:model-value="(v) => toggleRow(r, v)" />
					</td>
					<td v-for="col in columns" :key="col.key"
						:class="[{ right: col.align === 'right' || col.numeric, num: col.numeric }, frozenClass(col.key)]"
						:style="frozenStyle(col.key)">
						<slot :name="`cell-${col.key}`" :row="r" :value="r[col.key]">{{ r[col.key] }}</slot>
					</td>
					<td v-if="$slots['row-actions']" class="row-actions">
						<div class="actions-inner" :aria-label="rowLabel(r) ? `Actions for ${rowLabel(r)}` : undefined">
							<slot name="row-actions" :row="r" :label="rowLabel(r)" />
						</div>
					</td>
				</tr>
			</tbody>
		</table>
	</div>
</template>

<style scoped>
.l-table-wrap
{
	overflow-x: auto;
	position: relative;
}

.sticky-head thead th
{
	position: sticky;
	top: 0;
	z-index: 2;
	background: var(--surface);
}

.l-table
{
	width: 100%;
	border-collapse: collapse;
}

thead th
{
	font-size: var(--text-micro);
	/* font-weight: 600; */
	font-weight: 800;
	letter-spacing: 0.9px;
	text-transform: uppercase;
	color: var(--text-3);
	text-align: left;
	padding: 8px var(--sp-3);
	border-bottom: 1px solid var(--line-strong);
	white-space: nowrap;
	background: var(--surface);
}

th.right,
td.right
{
	text-align: right;
}

.th-btn
{
	display: inline-flex;
	align-items: center;
	gap: 4px;
	font: inherit;
	color: inherit;
	letter-spacing: inherit;
	text-transform: inherit;
	background: none;
	border: 0;
	cursor: pointer;
	min-height: var(--target-min);
}

th.right .th-btn
{
	flex-direction: row-reverse;
}

.th-btn .idle
{
	color: var(--decorative);
}

tbody td
{
	font-size: var(--text-base);
	color: var(--text);
	padding: 11px var(--sp-3);
	border-bottom: 1px solid var(--line-soft);
	background: var(--surface);
}

tbody tr:hover td
{
	background: var(--surface-sunken);
}

tbody tr.on td
{
	background: var(--interactive-tint);
}

td.num
{
	font-variant-numeric: tabular-nums;
	font-feature-settings: 'tnum' 1;
	font-weight: 600;
	color: var(--text-2);
}

.sel
{
	width: 20px;
}

/* frozen columns — opaque bg (they overlay scrolling content) + edge shadow */
.frozen
{
	background: var(--surface);
}

.frozen-edge::after
{
	content: '';
	position: absolute;
	top: 0;
	right: 0;
	bottom: -1px;
	width: 10px;
	transform: translateX(100%);
	pointer-events: none;
	background: linear-gradient(to right, var(--edge-shadow), transparent);
}

/* row actions — always visible, quiet until hover/focus-within */
.row-actions
{
	text-align: right;
	width: 1%;
	white-space: nowrap;
}

.actions-inner
{
	display: inline-flex;
	gap: 4px;
	color: var(--text-3);
}

tr:hover .actions-inner,
.actions-inner:focus-within
{
	color: var(--text);
}

.actions-head
{
	width: 1%;
}

.sr-only
{
	position: absolute;
	width: 1px;
	height: 1px;
	padding: 0;
	margin: -1px;
	overflow: hidden;
	clip: rect(0, 0, 0, 0);
	white-space: nowrap;
	border: 0;
}
</style>
